import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackDepositCompleted } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [bookingDetails, setBookingDetails] = useState(null);
  const sessionId = searchParams.get('session_id');
  const conversionFiredRef = useRef(false);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setBookingDetails(response.data);
        
        // Fire Google Ads conversion event ONCE
        if (!conversionFiredRef.current) {
          conversionFiredRef.current = true;
          trackDepositCompleted(
            response.data.booking_id || sessionId,
            response.data.course_name || 'Course Booking'
          );
        }
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <>
        <Helmet>
          <title>Processing Payment | Train Secure</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center p-4">
          <div className="bg-[#111827] p-8 rounded-lg shadow-lg text-center max-w-md w-full border border-gray-800" data-testid="checking-payment">
            <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Checking Payment Status...</h1>
            <p className="text-gray-400">Please wait while we confirm your payment</p>
          </div>
        </div>
      </>
    );
  }

  if (status === 'success') {
    return (
      <>
        <Helmet>
          <title>Booking Confirmed | Train Secure</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center p-4">
          <div className="bg-[#111827] p-8 rounded-lg shadow-lg text-center max-w-md w-full border border-gray-800" data-testid="payment-success">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h1>
            <p className="text-gray-400 mb-6">
              Thank you for your deposit payment. You will receive a confirmation email with all the details and an invoice for the remaining balance.
            </p>
            {bookingDetails?.course_name && (
              <div className="bg-[#0a0e1a] p-4 rounded-lg mb-6 text-left">
                <p className="text-sm text-gray-400">Course:</p>
                <p className="text-white font-medium">{bookingDetails.course_name}</p>
              </div>
            )}
            <Button
              data-testid="back-home-btn"
              onClick={() => navigate('/')}
              className="w-full bg-cyan-500 text-white hover:bg-cyan-600 py-6"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Payment Issue | Train Secure</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center p-4">
        <div className="bg-[#111827] p-8 rounded-lg shadow-lg text-center max-w-md w-full border border-gray-800" data-testid="payment-error">
          <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
          <p className="text-gray-400 mb-6">
            {status === 'timeout' && 'Payment verification timed out. Please check your email for confirmation or contact us.'}
            {status === 'expired' && 'Your payment session has expired. Please try again.'}
            {status === 'error' && 'There was an error processing your payment. Please contact us for assistance.'}
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-cyan-500 text-white hover:bg-cyan-600 py-6"
            >
              Back to Home
            </Button>
            <a href="https://wa.me/447435177049" className="block">
              <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-gray-800">
                Contact Us on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
